import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SectionHero from '@/components/feature/sections/SectionHero';
import SectionTrustBar from '@/components/feature/sections/SectionTrustBar';
import SectionContentOverview from '@/components/feature/sections/SectionContentOverview';
import SectionAIBlock from '@/components/feature/sections/SectionAIBlock';
import SectionProcess from '@/components/feature/sections/SectionProcess';
import SectionWhyChooseUs from '@/components/feature/sections/SectionWhyChooseUs';
import SectionServiceAreas from '@/components/feature/sections/SectionServiceAreas';
import SectionTestimonials from '@/components/feature/sections/SectionTestimonials';
import SectionFAQ from '@/components/feature/sections/SectionFAQ';
import SectionFinalCTA from '@/components/feature/sections/SectionFinalCTA';

const ABOUT_FAQS = [
  { q: 'How long has All Done Roofing LLC been in business?', a: 'All Done Roofing LLC has built its reputation by consistently delivering high-quality roofing services across the Mississippi Gulf Coast. We have established strong relationships with homeowners and businesses throughout Gulfport, Biloxi, Ocean Springs, and surrounding communities.' },
  { q: 'What makes All Done Roofing LLC different from other contractors?', a: 'We are GAF Certified and Fortification Certified — two of the most respected credentials in the roofing industry. We also specialize in insurance claim assistance, ensuring our clients receive the full coverage they deserve. Our approach is honest, transparent, and focused on long-term results.' },
  { q: 'Are you licensed and insured in Mississippi?', a: 'Yes. All Done Roofing LLC is fully licensed and insured in the state of Mississippi. We maintain all required certifications and coverage for residential and commercial roofing work across the Gulf Coast.' },
  { q: 'Do you work with homeowners insurance?', a: 'Absolutely. Insurance claim assistance is one of our core specialties. We document storm and wind damage thoroughly, coordinate directly with adjusters, and advocate on our clients\' behalf throughout the entire claims process.' },
  { q: 'What areas does All Done Roofing LLC serve?', a: 'We serve the Mississippi Gulf Coast including Gulfport, Biloxi, Ocean Springs, Long Beach, Diamondhead, Bay St. Louis, Waveland, D\'Iberville, Saucier, and surrounding communities. Our team understands the specific roofing demands of the Gulf Coast climate.' },
  { q: 'What roofing certifications does All Done Roofing hold?', a: 'All Done Roofing LLC is GAF Certified and Fortification Certified. GAF Certification confirms we meet the highest installation quality standards. Fortification Certification means we install roofs to exceed standard code requirements for storm and wind resistance.' },
];

const COMPANY_FEATURES = [
  { icon: 'ri-medal-2-fill', title: 'GAF Certified Contractor', desc: 'Meeting the highest installation quality standards recognized by the industry\'s leading manufacturer.' },
  { icon: 'ri-award-fill', title: 'Fortification Certified', desc: 'Building roofs to exceed standard code for storm and wind resistance — critical for Gulf Coast properties.' },
  { icon: 'ri-shield-check-fill', title: 'Licensed & Insured', desc: 'Fully licensed and insured in Mississippi for all residential and commercial roofing work.' },
  { icon: 'ri-file-list-3-fill', title: 'Insurance Claim Specialists', desc: 'Deep expertise in documentation, adjuster coordination, and claim advocacy for storm-damaged properties.' },
  { icon: 'ri-group-fill', title: 'Experienced Local Crew', desc: 'A dedicated Gulf Coast team that understands the specific demands of coastal roofing firsthand.' },
  { icon: 'ri-map-pin-2-fill', title: 'Gulf Coast Community', desc: 'Serving Gulfport, Biloxi, Ocean Springs, Long Beach, Diamondhead, Bay St. Louis, and beyond.' },
];

export default function AboutPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <SectionHero
          eyebrow="Mississippi Gulf Coast Roofing Contractor"
          headline="A ROOFING COMPANY"
          accentLine="Built on Quality & Trust."
          subheadline="Delivering dependable roofing solutions with a focus on craftsmanship, certified installation, and long-term value — across the Mississippi Gulf Coast."
          image="https://public.readdy.ai/ai/img_res/3a020f3ea50d8674a79503751130c487.jpg"
          imageAlt="All Done Roofing LLC - Gulf Coast Roofing Contractor"
          cta="Request a Free Estimate"
        />

        {/* 2. Trust Bar */}
        <SectionTrustBar />

        {/* 3. Company Overview */}
        <SectionContentOverview
          eyebrow="About All Done Roofing LLC"
          title="Who We Are"
          titleAccent="What We Stand For."
          paragraphs={[
            '<strong>All Done Roofing LLC</strong> is a Mississippi Gulf Coast roofing contractor built on a foundation of quality craftsmanship, honest service, and certified expertise.',
            'We specialize in roof replacement, repairs, storm damage restoration, insurance claim assistance, and commercial roofing — serving homeowners and businesses throughout Gulfport, Biloxi, Ocean Springs, Long Beach, Diamondhead, Bay St. Louis, and surrounding communities.',
            'As a <strong>GAF Certified and Fortification Certified</strong> contractor, every project we complete meets the highest standards for material quality, installation precision, and storm resistance. We build roofs that are designed to protect your property for decades against the Gulf Coast\'s demanding climate.',
            'Our approach is straightforward: honest estimates with no hidden charges, clean job sites, certified workmanship, and a final walkthrough with warranty documentation before we consider any project complete.',
          ]}
          features={COMPANY_FEATURES}
          image="https://readdy.ai/api/search-image?query=professional%20roofing%20contractor%20team%20All%20Done%20Roofing%20LLC%20Mississippi%20Gulf%20Coast%20residential%20project%20beautiful%20new%20dark%20charcoal%20shingle%20roof%20installation%20craftsmanship%20detail%20warm%20sunlight%20editorial%20quality%20photography&width=800&height=1000&seq=about_overview_01&orientation=portrait"
          imageAlt="All Done Roofing LLC team and craftsmanship"
        />

        {/* 4. AI Answer Block */}
        <SectionAIBlock
          eyebrow="About All Done Roofing LLC"
          heading="Premium Gulf Coast"
          headingAccent="Roofing Contractor"
          paragraphs={[
            '<strong class="text-white/90">All Done Roofing LLC</strong> is a GAF Certified and Fortification Certified roofing contractor serving residential and commercial properties throughout the Mississippi Gulf Coast.',
            'We specialize in roof replacement, repairs, storm damage restoration, and insurance claim assistance — delivering durable roofing systems built for the humidity, salt air, hurricane-force winds, and tropical storm systems that define Gulf Coast conditions.',
            'Our team brings local expertise, certified quality, and a customer-first approach to every project — whether we\'re replacing a single-family home roof in Gulfport or managing a commercial restoration project for a business in Biloxi.',
          ]}
        />

        {/* 5. Process */}
        <SectionProcess />

        {/* 6. Why Choose Us */}
        <SectionWhyChooseUs />

        {/* 7. Service Areas */}
        <SectionServiceAreas />

        {/* 8. Testimonials */}
        <SectionTestimonials />

        {/* 9. FAQ */}
        <SectionFAQ
          faqs={ABOUT_FAQS}
          title="About All Done Roofing"
          subtitle="Common questions about our company, certifications, experience, and service areas."
        />

        {/* 10. Final CTA */}
        <SectionFinalCTA
          headline="BUILT TO LAST."
          accentLine="Your roof. Our reputation."
          subheadline="All Done Roofing LLC puts our name on every project we complete. That means GAF-certified materials, Fortification-certified installation, honest estimates, and a final result we're proud to stand behind — on every single job."
          primaryCTA="Request a Free Estimate"
          image="https://readdy.ai/api/search-image?query=professional%20roofers%20standing%20proud%20completed%20premium%20dark%20charcoal%20residential%20roof%20beautiful%20Mississippi%20Gulf%20Coast%20coastal%20neighborhood%20warm%20golden%20sunlight%20team%20portrait%20editorial%20quality&width=1920&height=900&seq=about_cta_01&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}
